/* ==========================================================================
   ASD runtime for the DeemedFit homepage
   One file, no dependencies. Observers and class toggles only, so nothing
   here blocks the main thread or reads layout on scroll.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function money(cents) { return '$' + (cents / 100).toFixed(2).replace(/\.00$/, ''); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ------------------------------------------------------------------------
     1. REVEAL - one observer, each element unobserved once it lands
     ------------------------------------------------------------------------ */
  function initReveal() {
    var nodes = $all('.asd-reveal');
    if (!nodes.length) return;
    if (REDUCED || !('IntersectionObserver' in window)) { nodes.forEach(function (n) { n.classList.add('is-in'); }); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.04 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ------------------------------------------------------------------------
     2. CAROUSEL - the browser scrolls; this paints the bar and the arrows
     ------------------------------------------------------------------------ */
  function initCarousel(root) {
    var track = root.querySelector('.asd-carousel__track');
    var bar = root.querySelector('.asd-carousel__progress');
    var fill = root.querySelector('.asd-carousel__progress-fill');
    var prev = root.querySelector('[data-carousel-prev]');
    var next = root.querySelector('[data-carousel-next]');
    if (!track) return;
    var queued = false;

    function paint() {
      queued = false;
      var max = track.scrollWidth - track.clientWidth;
      var scrollable = max > 1;
      if (bar) bar.hidden = !scrollable;
      var nav = root.querySelector('.asd-carousel__nav');
      if (nav) nav.style.visibility = scrollable ? '' : 'hidden';
      if (fill && scrollable) {
        var ratio = Math.max(0.12, track.clientWidth / track.scrollWidth);
        var pct = ratio * 100;
        var progress = Math.min(1, Math.max(0, track.scrollLeft / max));
        fill.style.width = pct + '%';
        fill.style.transform = 'translateX(' + (progress * (100 - pct) / ratio) + '%)';
      }
      if (prev) prev.disabled = !scrollable || track.scrollLeft <= 2;
      if (next) next.disabled = !scrollable || track.scrollLeft >= max - 2;
    }
    function schedule() { if (!queued) { queued = true; requestAnimationFrame(paint); } }
    function step(dir) {
      var item = track.querySelector('.asd-carousel__item:not([hidden])');
      var gap = parseFloat(getComputedStyle(track).columnGap) || 12;
      var amount = item ? item.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
      track.scrollBy({ left: dir * amount, behavior: REDUCED ? 'auto' : 'smooth' });
    }

    track.addEventListener('scroll', schedule, { passive: true });
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
    if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(track);
    else window.addEventListener('resize', schedule);
    root._asdRefresh = function () { track.scrollLeft = 0; schedule(); };
    paint();
  }

  /* ------------------------------------------------------------------------
     3. FILTER CHIPS - show only the cards tagged for the chosen profession
     ------------------------------------------------------------------------ */
  function initFilters() {
    $all('[data-filter-group]').forEach(function (group) {
      var target = document.getElementById(group.getAttribute('data-filter-group'));
      if (!target) return;
      var items = $all('[data-tags]', target);
      var empty = target.querySelector('[data-filter-empty]');
      group.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-filter]');
        if (!chip) return;
        var key = chip.getAttribute('data-filter');
        $all('[data-filter]', group).forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
        var shown = 0;
        items.forEach(function (it) {
          var match = key === 'all' || (' ' + it.getAttribute('data-tags') + ' ').indexOf(' ' + key + ' ') > -1;
          it.hidden = !match;
          if (match) shown++;
        });
        if (empty) empty.hidden = shown > 0;
        if (target._asdRefresh) target._asdRefresh();
      });
    });
  }

  /* ------------------------------------------------------------------------
     4. BUNDLE PICKER - radio tiers update the live total and saving
     ------------------------------------------------------------------------ */
  function initBundle() {
    $all('[data-bundle]').forEach(function (root) {
      var base = parseInt(root.getAttribute('data-base'), 10) || 6500;
      var total = root.querySelector('[data-bundle-total]');
      var save = root.querySelector('[data-bundle-save]');
      var each = root.querySelector('[data-bundle-each]');
      function update() {
        var picked = root.querySelector('input[type="radio"]:checked');
        if (!picked) return;
        var qty = parseInt(picked.getAttribute('data-qty'), 10);
        var unit = parseInt(picked.getAttribute('data-unit'), 10);
        var saving = base * qty - unit * qty;
        if (total) total.textContent = money(unit * qty);
        if (each) each.textContent = qty + (qty === 1 ? ' piece at ' : ' pieces at ') + money(unit) + ' each';
        if (save) save.textContent = saving > 0 ? 'You save ' + money(saving) : 'Standard price';
      }
      root.addEventListener('change', update);
      update();
    });
  }

  /* ------------------------------------------------------------------------
     5. FAQ - native <details>. Opening one closes its siblings.
     ------------------------------------------------------------------------ */
  function initFaq() {
    $all('[data-faq]').forEach(function (group) {
      var items = $all('details', group);
      items.forEach(function (d) {
        d.addEventListener('toggle', function () {
          if (!d.open) return;
          items.forEach(function (o) { if (o !== d) o.open = false; });
        });
      });
    });
  }

  /* ------------------------------------------------------------------------
     6. PANELS - cart drawer and mobile menu
     ------------------------------------------------------------------------ */
  var Panels = (function () {
    var overlay = null, current = null, lastTrigger = null;
    function getOverlay() {
      if (overlay) return overlay;
      overlay = document.querySelector('[data-asd-overlay]');
      overlay.addEventListener('click', function () { close(); });
      return overlay;
    }
    function open(id, trigger) {
      var panel = document.getElementById(id);
      if (!panel) return;
      if (current && current !== panel) close(true);
      lastTrigger = trigger || null;
      getOverlay().classList.add('is-open');
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      panel.removeAttribute('inert');
      document.body.classList.add('asd-no-scroll');
      $all('[aria-controls="' + id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      current = panel;
      var f = panel.querySelector('[data-panel-focus]');
      if (f) setTimeout(function () { f.focus(); }, 80);
    }
    function close(silent) {
      if (!current) return;
      var id = current.id;
      current.classList.remove('is-open');
      current.setAttribute('aria-hidden', 'true');
      current.setAttribute('inert', '');
      $all('[aria-controls="' + id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      current = null;
      if (!silent) {
        getOverlay().classList.remove('is-open');
        document.body.classList.remove('asd-no-scroll');
        if (lastTrigger && lastTrigger.focus) lastTrigger.focus();
      }
      lastTrigger = null;
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
      if (e.key !== 'Tab' || !current) return;
      var f = $all('a[href], button:not([disabled]), input:not([disabled])', current);
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    });
    return { open: open, close: close, current: function () { return current; } };
  })();

  /* ------------------------------------------------------------------------
     7. HEADER - sentinel driven, no scroll handler
     ------------------------------------------------------------------------ */
  function initHeader() {
    var header = document.querySelector('[data-asd-header]');
    var sentinel = document.querySelector('[data-header-sentinel]');
    if (!header || !sentinel || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (e) { header.classList.toggle('is-stuck', !e[0].isIntersecting); }).observe(sentinel);
  }

  /* ------------------------------------------------------------------------
     8. DELEGATED CLICKS
     ------------------------------------------------------------------------ */
  function initEvents() {
    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-panel-open],[data-panel-close]');
      if (!t) return;
      if (t.hasAttribute('data-panel-open')) { e.preventDefault(); Panels.open(t.getAttribute('data-panel-open'), t); return; }
      if (t.hasAttribute('data-panel-close')) { Panels.close(); }
    });

    $all('[data-newsletter]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var input = form.querySelector('input[type="email"]');
        var note = form.parentNode.querySelector('[data-newsletter-note]');
        var ok = input && /.+@.+\..+/.test(input.value);
        if (input) input.setAttribute('aria-invalid', ok ? 'false' : 'true');
        if (note) {
          note.hidden = false;
          note.textContent = ok ? 'You are on the list. Watch for the next drop.' : 'Enter a valid email address.';
          note.classList.toggle('is-error', !ok);
        }
        if (ok) form.reset();
      });
    });
  }

  function boot() {
    $all('.asd-panel').forEach(function (p) { p.setAttribute('inert', ''); });
    initHeader();
    initEvents();
    $all('[data-asd-carousel]').forEach(initCarousel);
    initFilters();
    initBundle();
    initFaq();
    initReveal();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  window.ASD = { Panels: Panels };
})();
